import { supabase } from '@/integrations/supabase/client';
import type { ProjectPDFInput } from '@/lib/generateProjectPDF';
import type { BuildBuyEvaluationRow } from '@/hooks/useBuildBuyEvaluation';

/**
 * Fetches everything needed to render the full per-project PDF report for many
 * projects at once, using a constant number of queries (independent of N).
 */
export async function fetchBulkProjectExportData(projectIds: string[]): Promise<ProjectPDFInput[]> {
  if (!projectIds.length) return [];

  const { data: projects, error: projErr } = await supabase
    .from('participant_projects')
    .select('*')
    .in('id', projectIds);
  if (projErr) throw projErr;
  if (!projects || projects.length === 0) return [];

  const userIds = Array.from(new Set(projects.map((p: any) => p.user_id).filter(Boolean)));
  const cohortIds = Array.from(new Set(projects.map((p: any) => p.cohort_id).filter(Boolean)));
  const pIds = projects.map((p: any) => p.id);

  const [profilesRes, cohortsRes, toolsRes, evalsRes, delivRes, bbRes, sugRes] = await Promise.all([
    userIds.length
      ? supabase.from('profiles').select('id, full_name, email, company, area').in('id', userIds)
      : Promise.resolve({ data: [], error: null } as any),
    cohortIds.length
      ? supabase.from('cohorts').select('id, name').in('id', cohortIds)
      : Promise.resolve({ data: [], error: null } as any),
    supabase.from('project_tools').select('project_id, tool_name').in('project_id', pIds),
    supabase.from('project_evaluations').select('*').in('project_id', pIds),
    userIds.length
      ? supabase.from('participant_deliverables').select('*').in('user_id', userIds).order('submitted_at', { ascending: false })
      : Promise.resolve({ data: [], error: null } as any),
    supabase
      .from('build_buy_evaluations' as any)
      .select('*')
      .in('project_id', pIds)
      .order('created_at', { ascending: false }),
    supabase.from('project_suggestions').select('*').in('project_id', pIds).order('created_at', { ascending: false }),
  ]);

  for (const r of [profilesRes, cohortsRes, toolsRes, evalsRes, delivRes, bbRes, sugRes]) {
    if ((r as any).error) throw (r as any).error;
  }

  const profileMap = new Map<string, any>((profilesRes.data || []).map((p: any) => [p.id, p]));
  const cohortMap = new Map<string, any>((cohortsRes.data || []).map((c: any) => [c.id, c]));

  const toolsMap = new Map<string, string[]>();
  for (const t of (toolsRes.data || []) as any[]) {
    const arr = toolsMap.get(t.project_id) || [];
    arr.push(t.tool_name);
    toolsMap.set(t.project_id, arr);
  }

  const evalMap = new Map<string, any>();
  for (const e of (evalsRes.data || []) as any[]) evalMap.set(e.project_id, e);

  // Deliverables are scoped per user; pdf groups them per project's owner.
  const delivByUser = new Map<string, any[]>();
  for (const d of (delivRes.data || []) as any[]) {
    const arr = delivByUser.get(d.user_id) || [];
    arr.push(d);
    delivByUser.set(d.user_id, arr);
  }

  // Latest build/buy per project (results are already ordered desc).
  const bbMap = new Map<string, BuildBuyEvaluationRow>();
  for (const b of (bbRes.data || []) as any[]) {
    if (!bbMap.has(b.project_id)) bbMap.set(b.project_id, b as BuildBuyEvaluationRow);
  }

  const sugMap = new Map<string, any[]>();
  for (const s of (sugRes.data || []) as any[]) {
    const arr = sugMap.get(s.project_id) || [];
    arr.push(s);
    sugMap.set(s.project_id, arr);
  }

  // Preserve the order the caller requested.
  const projById = new Map<string, any>(projects.map((p: any) => [p.id, p]));
  const ordered = projectIds.map((id) => projById.get(id)).filter(Boolean);

  return ordered.map((project: any): ProjectPDFInput => {
    const profile = profileMap.get(project.user_id) || null;
    const cohort = project.cohort_id ? cohortMap.get(project.cohort_id) || null : null;
    const ev = evalMap.get(project.id);
    return {
      project,
      participant: profile
        ? { full_name: profile.full_name, email: profile.email, company: profile.company, area: profile.area }
        : null,
      cohort: cohort ? { name: cohort.name } : null,
      tools: toolsMap.get(project.id) || [],
      evaluation: ev
        ? {
            total_score: ev.total_score ?? null,
            classification: ev.classification ?? null,
            component_scores: ev.component_scores ?? null,
            general_comments: ev.general_comments ?? null,
            suggestions: ev.suggestions ?? null,
          }
        : null,
      deliverables: delivByUser.get(project.user_id) || [],
      buildBuy: bbMap.get(project.id) || null,
      suggestions: sugMap.get(project.id) || [],
    };
  });
}
